import { Extension } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"
import { Decoration, DecorationSet } from "@tiptap/pm/view"
import Suggestion from "@tiptap/suggestion"

import type { CloseLoopPlaceholder } from "@productlane/lib/src/close-loop-template"
import type { SlackWelcomeKind } from "@productlane/lib/src/slack-welcome"
import { SLACK_WELCOME_VARIABLES } from "@productlane/lib/src/slack-welcome"

import type { SuggestionOptions } from "@tiptap/suggestion"
import { createPlaceholderRenderer } from "./close-loop-placeholders/suggestion"

export const slackWelcomeToken = (key: string) => `{${key}}`

// A closed brace is the finished token, so the list steps aside.
export function filterSlackWelcomeVariables(
	kind: SlackWelcomeKind,
	query: string,
): CloseLoopPlaceholder[] {
	if (query.includes("}")) return []
	const needle = query.trim().toLowerCase()
	return SLACK_WELCOME_VARIABLES[kind]
		.filter((variable) => !needle || variable.key.startsWith(needle))
		.map((variable) => ({ key: variable.key, label: variable.description }))
}

const tokenPattern = (kind: SlackWelcomeKind) =>
	new RegExp(
		`\\{(?:${SLACK_WELCOME_VARIABLES[kind].map((v) => v.key).join("|")})\\}`,
		"g",
	)

export interface SlackWelcomeVariableOptions {
	kind: SlackWelcomeKind
	render: SuggestionOptions<CloseLoopPlaceholder, { key: string }>["render"]
}

export const SlackWelcomeVariableSuggestion =
	Extension.create<SlackWelcomeVariableOptions>({
		name: "slackWelcomeVariableSuggestion",

		addOptions() {
			return {
				kind: "channel",
				render: createPlaceholderRenderer(slackWelcomeToken),
			}
		},

		addProseMirrorPlugins() {
			const { kind, render } = this.options
			const pattern = tokenPattern(kind)
			return [
				Suggestion<CloseLoopPlaceholder, { key: string }>({
					editor: this.editor,
					char: "{",
					pluginKey: new PluginKey(`slackWelcomeVariables-${kind}`),
					allowSpaces: false,
					// A variable can sit right after text, like "Hi{".
					allowedPrefixes: null,
					items: ({ query }) => filterSlackWelcomeVariables(kind, query),
					render,
					command: ({ editor, range, props }) =>
						editor
							.chain()
							.focus()
							.insertContentAt(range, slackWelcomeToken(props.key))
							.run(),
				}),
				new Plugin({
					key: new PluginKey(`slackWelcomeVariableHighlight-${kind}`),
					props: {
						decorations(state) {
							const decorations: Decoration[] = []
							state.doc.descendants((node, pos) => {
								if (!node.isText || !node.text) return
								for (const match of node.text.matchAll(pattern)) {
									const from = pos + (match.index ?? 0)
									decorations.push(
										Decoration.inline(from, from + match[0].length, {
											class:
												"rounded bg-gray-100 px-0.5 font-mono text-[12.5px] text-secondary dark:bg-gray-800",
										}),
									)
								}
							})
							return DecorationSet.create(state.doc, decorations)
						},
					},
				}),
			]
		},
	})
